import { createApi } from '@reduxjs/toolkit/query/react';

import { DriverApi, Driver, NewDriver } from '../models';
import { mapDriverApiToDriver } from '../adapters';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { createBaseQueryWithReauth } from '@/services';

import { API_FLEET_URL } from '@/utils';

const driversService = createApi({
  reducerPath: 'driversService',
  baseQuery: createBaseQueryWithReauth(`${API_FLEET_URL}/drivers`),
  tagTypes: ['Driver', 'Drivers'],
  endpoints: (builder) => ({
    getDriver: builder.query<Driver, { driverId: string }>({
      query: ({ driverId }) => ({
        url: `/${driverId}`,
      }),
      transformResponse: (response: DriverApi) =>
        mapDriverApiToDriver(response),
      providesTags: ['Driver'],
    }),
    getDrivers: builder.query<
      PaginatedResponse<Driver>,
      {
        companyId?: string;
        search?: string;
        phone?: string;
        paginated: PaginatedRequest;
      }
    >({
      query: ({ companyId, search, phone, paginated }) => ({
        url: '',
        params: {
          ...(companyId && { companyId }),
          ...(search && { fullName: search }),
          ...(phone && { phone }),
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      transformResponse: (response: PaginatedResponse<DriverApi>) => ({
        ...response,
        items: response.items.map(mapDriverApiToDriver),
      }),
      providesTags: ['Drivers'],
    }),
    createDriver: builder.mutation<Driver, NewDriver>({
      query: (data) => ({
        url: '',
        method: 'POST',
        body: { ...data, umDriverId: 'default' },
      }),
      invalidatesTags: ['Drivers'],
    }),
    updateDriver: builder.mutation<
      Driver,
      { driverId: string; data: Partial<Driver> }
    >({
      query: ({ driverId, data }) => ({
        url: `/${driverId}`,
        method: 'PUT',
        body: { ...data, umDriverId: 'default' },
      }),
      invalidatesTags: ['Driver', 'Drivers'],
    }),
    deleteDriver: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Driver', 'Drivers'],
    }),
  }),
});

export default driversService;

export const {
  useGetDriverQuery,
  useGetDriversQuery,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useDeleteDriverMutation,
} = driversService;
